import { DateRange } from '@angular/material/datepicker';
import { createReducer, on } from '@ngrx/store';
import moment from 'moment';

import { TrendingActions } from './trending.actions';
import { TrendingResolution, TrendingTypeEnum } from './trending.model';
import { TrendingState } from './trending.state';

export const initTrendingState: TrendingState = {
  dateRange: new DateRange(
    moment().startOf('day').subtract('6', 'days').toDate(),
    moment().toDate()
  ),
  resolution: TrendingResolution.Hour,
  dataSeries: null,
  chartsSelectedProperties: [],
  loading: false,
  loaded: false,
  error: null,
  dateseriesError: null,
  navigatorMin: null,
  navigatorMax: null,
  trendingType: TrendingTypeEnum.HEATING,
  trendingTabData: [],
  energyStorageTrendingLabel: {
    powerTrendingChartsLabel: [],
    stateOfChargeLabel: [],
    temperaturesLabel: [],
    batteryModuleVoltageLabel: [],
  },
  visiblePlotBandFeatures: [],
};

export const trendingReducer = createReducer(
  initTrendingState,

  on(
    TrendingActions.getDataSeries,
    (state): TrendingState => ({
      ...state,
      loading: true,
      loaded: false,
    })
  ),

  on(
    TrendingActions.getDataSeriesSuccess,
    (state, { dataSeries }): TrendingState => ({
      ...state,
      dataSeries,
      chartsSelectedProperties: [],
      loading: false,
      loaded: true,
    })
  ),

  on(
    TrendingActions.getDataSeriesError,
    (state, { error }): TrendingState => ({
      ...state,
      dataSeries: null,
      loaded: true,
      loading: false,
      dateseriesError: error,
    })
  ),

  on(
    TrendingActions.selectChartProperties,
    (state, { chartProperties }): TrendingState => {
      const existingChartProperties = state.chartsSelectedProperties.find(
        (entry) => entry.chartName === chartProperties.chartName
      );

      return {
        ...state,
        chartsSelectedProperties: existingChartProperties
          ? state.chartsSelectedProperties.map((properties) =>
              properties.chartName === chartProperties.chartName ? chartProperties : properties
            )
          : [...state.chartsSelectedProperties, chartProperties],
      };
    }
  ),

  on(
    TrendingActions.clearStore,
    (): TrendingState => ({
      ...initTrendingState,
      dataSeries: null,
      error: null,
      dateseriesError: null,
    })
  ),

  on(
    TrendingActions.setDateRange,
    (state, { dateRange }): TrendingState => ({
      ...state,
      dateRange,
    })
  ),

  on(
    TrendingActions.setResolution,
    (state, { resolution }): TrendingState => ({
      ...state,
      resolution,
    })
  ),
  on(
    TrendingActions.setNavigatorExtremes,
    (state, { min, max }): TrendingState => ({
      ...state,
      navigatorMin: min,
      navigatorMax: max,
    })
  ),

  on(
    TrendingActions.selectTrendingTabData.success,
    (state, { trendingTabData }): TrendingState => ({
      ...state,
      trendingTabData,
    })
  ),

  on(
    TrendingActions.setActiveTrendingType,
    (state, { trendingType }): TrendingState => ({
      ...state,
      trendingTabData: state.trendingTabData.map((tab) => {
        return {
          ...tab,
          isActive: tab.type === trendingType ? true : false,
        };
      }),
    })
  ),

  on(
    TrendingActions.getEnergyStorageLabels.success,
    (state, { energyStorageTrendingLabel }): TrendingState => ({
      ...state,
      energyStorageTrendingLabel,
    })
  ),

  on(
    TrendingActions.getEnergyStorageLabels.error,
    (state, { error }): TrendingState => ({
      ...state,
      energyStorageTrendingLabel: {
        powerTrendingChartsLabel: [],
        stateOfChargeLabel: [],
        temperaturesLabel: [],
        batteryModuleVoltageLabel: [],
      },
      error,
    })
  ),

  on(
    TrendingActions.selectPlotBandFeature,
    (state, { features }): TrendingState => {
      const visiblePlotBandFeatures = structuredClone(state.visiblePlotBandFeatures);

      features.forEach((feature) => {
        const matchingFeature = visiblePlotBandFeatures.find(
          (visiblePlotBandFeature) =>
            feature.chartName === visiblePlotBandFeature.chartName &&
            feature.feature === visiblePlotBandFeature.feature
        );
        if (matchingFeature) {
          // If feature is present - reverse its visibility
          matchingFeature.visible = !matchingFeature.visible;
        } else {
          // If feature is not present - add it to the store
          visiblePlotBandFeatures.push(feature);
        }
      });

      return {
        ...state,
        visiblePlotBandFeatures,
      };
    }
  ),

  on(
    TrendingActions.hidePlotBandFeaturesForGivenDevice,
    (state, { chartName }): TrendingState => {
      const visiblePlotBandFeatures = state.visiblePlotBandFeatures.map((plotBandFeature) => {
        if (plotBandFeature.chartName === chartName) {
          return { ...plotBandFeature, visible: false };
        } else {
          return plotBandFeature;
        }
      });

      return {
        ...state,
        visiblePlotBandFeatures,
      };
    }
  )
);
